import React from "react";
import { Link } from "react-router-dom";
import { Plane, BadgeCheck, HeartPulse, MapPin, Clock, ChevronRight, Stethoscope, ShieldCheck, Globe } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";

export default function MedicalTourismPage() {
    const { lang } = useI18n();

    const features = [
        {
            Icon: Stethoscope,
            title: lang === "ar" ? "مستشفيات ومتخصصون من الدرجة الأولى" : "Top-Tier Hospitals & Specialists",
            body: lang === "ar"
                ? "وصول فوري إلى أفضل المستشفيات والأطباء المتخصصين في مصر — مُختارون بعناية للجودة والمصداقية."
                : "Immediate access to Egypt's finest hospitals and specialist physicians — carefully selected for quality and credibility.",
        },
        {
            Icon: BadgeCheck,
            title: lang === "ar" ? "أسعار شفافة ومُحسَّنة" : "Transparent, Optimized Pricing",
            body: lang === "ar"
                ? "لا رسوم مخفية. نتفاوض نيابةً عنك للحصول على أفضل الأسعار مقارنةً بالعلاج خارج مصر."
                : "No hidden fees. We negotiate on your behalf for the best rates — significantly less than equivalent treatment abroad.",
        },
        {
            Icon: MapPin,
            title: lang === "ar" ? "السفر والإقامة واللوجستيات" : "Travel, Accommodation & Logistics",
            body: lang === "ar"
                ? "من حجز الفندق إلى تنظيم الانتقالات — نتولى كل شيء حتى تركّز أنت على صحتك."
                : "From hotel bookings to transfers and scheduling — we handle everything so you can focus on your health.",
        },
        {
            Icon: HeartPulse,
            title: lang === "ar" ? "تنسيق مريض مخصص" : "Dedicated Patient Coordination",
            body: lang === "ar"
                ? "منسّق شخصي يرافقك من التشخيص حتى الشفاء، بالعربية والإنجليزية."
                : "A personal coordinator with you from diagnosis to recovery, in Arabic and English.",
        },
    ];

    const benefits = lang === "ar" ? [
        "وفّر بشكل ملحوظ مقارنةً بتكاليف العلاج الدولي",
        "رحلة خالية من التوتر — مُدارة بالكامل من البداية للنهاية",
        "مقدمو رعاية صحية موثوقون ومُتحقَّق منهم",
        "وصول سريع — لا قوائم انتظار",
    ] : [
        "Save significantly vs international treatment costs",
        "Zero stress — fully managed journey from start to finish",
        "Trusted, verified healthcare providers",
        "Fast access — no waiting lists",
    ];

    const steps = [
        {
            num: "01",
            title: lang === "ar" ? "أخبرنا بحاجتك" : "Tell Us Your Needs",
            body: lang === "ar"
                ? "شارك معنا احتياجاتك الطبية ومواعيد السفر المناسبة لك."
                : "Share your medical needs and available travel dates with us.",
        },
        {
            num: "02",
            title: lang === "ar" ? "خطة مخصصة لك" : "Receive Your Plan",
            body: lang === "ar"
                ? "نُعدّ لك خطة شاملة تشمل المواعيد والتخصصات والإقامة."
                : "We prepare a tailored plan covering appointments, specialties, and accommodation.",
        },
        {
            num: "03",
            title: lang === "ar" ? "تأكيد وتنسيق كامل" : "Confirm & Coordinate",
            body: lang === "ar"
                ? "نؤكد جميع الترتيبات مسبقًا — أطباء، مرافق، واستيعاب."
                : "We confirm all arrangements in advance — doctors, facilities, and logistics.",
        },
        {
            num: "04",
            title: lang === "ar" ? "علاج ومتابعة" : "Treatment & Follow-Up",
            body: lang === "ar"
                ? "نستقبلك ونرافقك طوال رحلتك ونتابعك بعد عودتك."
                : "We welcome you, accompany you throughout, and follow up after your return.",
        },
    ];

    return (
        <div className="min-h-screen bg-background" data-testid="medical-tourism-page">
            <PublicHeader />

            <main className="pt-16">
                {/* Hero */}
                <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-background py-20 sm:py-28">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary mb-6">
                            <Plane className="h-3.5 w-3.5" />
                            {lang === "ar" ? "السياحة العلاجية في مصر" : "Medical Tourism in Egypt"}
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
                            {lang === "ar"
                                ? "علاج عالمي المستوى في مصر\nتجربة متكاملة من البداية للنهاية"
                                : "World-Class Treatment in Egypt\nFully Managed, Start to Finish"}
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
                            {lang === "ar"
                                ? "باقات علاجية متكاملة تجمع السفر والرعاية الصحية وتحسين التكاليف في تجربة واحدة سلسة. من التشخيص حتى التعافي — نتولى كل شيء."
                                : "Access world-class treatment in Egypt with fully managed packages, combining travel, care, and cost optimization into one seamless experience. From diagnosis to recovery — we handle everything."}
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                            >
                                {lang === "ar" ? "ابدأ رحلتك الآن" : "Start Your Journey"}
                                <ChevronRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* What We Handle */}
                <section className="py-16 sm:py-20">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-foreground mb-3">
                                {lang === "ar" ? "نتولى كل شيء نيابةً عنك" : "We Handle Everything For You"}
                            </h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                {lang === "ar"
                                    ? "من أول اتصال حتى آخر متابعة — فريقنا يضمن لك تجربة خالية من التعقيد."
                                    : "From first contact to final follow-up — our team ensures a completely stress-free experience."}
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {features.map(({ Icon, title, body }) => (
                                <div key={title} className="surface-card p-6 flex gap-4">
                                    <div className="shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Icon className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground mb-1">{title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Key Benefits */}
                <section className="py-16 sm:py-20 bg-muted/30">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-bold text-foreground mb-6">
                                    {lang === "ar" ? "لماذا تختار العلاج في مصر معنا؟" : "Why Choose Treatment in Egypt With Us?"}
                                </h2>
                                <ul className="space-y-4">
                                    {benefits.map((item) => (
                                        <li key={item} className="flex items-start gap-3">
                                            <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                            <span className="text-sm text-muted-foreground leading-relaxed">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    { value: lang === "ar" ? "حتى 70%" : "Up to 70%", label: lang === "ar" ? "توفير مقارنةً بأوروبا وأمريكا" : "Savings vs Europe & USA" },
                                    { value: lang === "ar" ? "500+" : "500+", label: lang === "ar" ? "مقدم رعاية صحية معتمد" : "Verified Healthcare Providers" },
                                    { value: lang === "ar" ? "أيام" : "Days", label: lang === "ar" ? "لا شهور انتظار" : "Not months of waiting" },
                                    { value: lang === "ar" ? "٢٤/٧" : "24/7", label: lang === "ar" ? "دعم طوال رحلتك" : "Support Throughout Your Journey" },
                                ].map(({ value, label }) => (
                                    <div key={label} className="surface-card p-5 text-center">
                                        <div className="text-2xl font-bold text-primary mb-1">{value}</div>
                                        <div className="text-xs text-muted-foreground leading-snug">{label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Works */}
                <section className="py-16 sm:py-20">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <h2 className="text-3xl font-bold text-foreground text-center mb-12">
                            {lang === "ar" ? "كيف تسير رحلتك معنا" : "How Your Journey Works"}
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {steps.map(({ num, title, body }) => (
                                <div key={num} className="text-center">
                                    <div className="text-4xl font-bold text-primary/20 mb-3">{num}</div>
                                    <h3 className="font-semibold text-foreground mb-2">{title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-16 sm:py-20 bg-primary/5">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                        <Globe className="h-10 w-10 text-primary mx-auto mb-4" />
                        <h2 className="text-3xl font-bold text-foreground mb-3">
                            {lang === "ar" ? "هل أنت مستعد للبدء؟" : "Ready to Get Started?"}
                        </h2>
                        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                            {lang === "ar"
                                ? "فريقنا جاهز للإجابة على جميع استفساراتك وتصميم باقة علاجية مخصصة لك."
                                : "Our team is ready to answer all your questions and design a tailored treatment package for you."}
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                            >
                                {lang === "ar" ? "تواصل مع فريقنا" : "Contact Our Team"}
                                <ChevronRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-8 py-3 text-sm font-semibold text-foreground hover:bg-accent transition-colors"
                            >
                                {lang === "ar" ? "سجّل للحصول على عضوية" : "Become a Member"}
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
